import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { personVars } from '@/lib/personColor';
import { de } from '@/i18n/de';

export interface SwatchProps {
  /** `null` renders the hatched "Nicht zugewiesen" swatch. */
  colorIndex: number | null;
  /** Edge length in pixels; the design uses 8, 10, 12, 14, 26 and 44. */
  size: number;
  radius?: number;
  /** Initials of persons already using this colour, shown in the picker. */
  overlay?: string;
  selected?: boolean;
  className?: string;
}

export function Swatch({
  colorIndex,
  size,
  radius,
  overlay,
  selected,
  className,
}: SwatchProps) {
  const style = {
    width: size,
    height: size,
    borderRadius: radius ?? Math.min(8, Math.round(size / 3)),
    ...(colorIndex === null ? {} : personVars(colorIndex)),
  };

  return (
    <span
      aria-hidden={overlay ? undefined : true}
      style={style}
      className={cn(
        'relative box-border inline-flex shrink-0 items-center justify-center',
        colorIndex === null ? 'hatch border border-dashed border-free-dash' : 'person-bg',
        selected && 'ring-select',
        className,
      )}
    >
      {overlay ? (
        <span className="font-mono text-[10px] font-semibold opacity-90">{overlay}</span>
      ) : null}
      {selected && size >= 24 ? <Check className="size-3.5" strokeWidth={3} /> : null}
    </span>
  );
}

/** Legend entry: swatch plus label, used under the month grid and in statistics. */
export function SwatchLabel({
  colorIndex,
  name,
  size = 10,
}: {
  colorIndex: number | null;
  name?: string;
  size?: number;
}) {
  return (
    <span className="inline-flex items-center gap-[5px]">
      <Swatch colorIndex={colorIndex} size={size} radius={3} />
      <span className="text-hint text-fg-tertiary">{name ?? de.modal.unassigned}</span>
    </span>
  );
}
